import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Text } from '@cuemath/web-comps';
import { COLORS } from '@cuemath/comps';
import { Save } from '@styled-icons/fa-solid/Save';
import { formatTime } from '../../../utils';

import {
  TRIAL_PARTS,
  ANNOTATION_EMOTION_OPTIONS,
} from '../../../constants/trial-annotation';

const FormContainer = styled.div`
  padding: 16px;
  border: 2px dashed ${COLORS.COLA};
  border-radius: 8px;
  background: ${COLORS.COLA_LIGHT};
  margin-bottom: 16px;
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const FormTitle = styled(Text)`
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 4px 8px;
  font-size: 16px;
  min-width: auto;
  height: 24px;
  background: transparent;
  color: ${COLORS.GRAY_DARK};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: ${COLORS.GRAY_LIGHT};
    color: ${COLORS.BLACK};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${COLORS.GRAY_DARK};
`;

const TimeDisplay = styled.div`
  font-family: monospace;
  font-size: 12px;
  background: ${COLORS.WHITE};
  padding: 8px;
  border-radius: 4px;
  border: 1px solid ${COLORS.GRAY};
  color: ${COLORS.BLACK};
`;

const Select = styled.select`
  padding: 6px 8px;
  border: 1px solid ${COLORS.GRAY};
  border-radius: 4px;
  background: ${COLORS.WHITE};
  font-size: 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px;
  border: 1px solid ${COLORS.GRAY};
  border-radius: 4px;
  font-size: 12px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: ${COLORS.COLA};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const SaveButton = styled(Button)`
  background: ${COLORS.COLA};
  color: ${COLORS.WHITE};
  padding: 8px 16px;
  font-size: 13px;
  height: 36px;
  border-radius: 6px;
  font-weight: 500;
  border: none;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: ${COLORS.COLA_DARK};
  }

  &:disabled {
    background: ${COLORS.GRAY};
    color: ${COLORS.GRAY_DARK};
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${COLORS.WHITE};
  color: ${COLORS.GRAY_DARK};
  border: 1px solid ${COLORS.GRAY};
  padding: 8px 16px;
  font-size: 13px;
  height: 36px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: ${COLORS.GRAY_LIGHT};
    border-color: ${COLORS.GRAY_DARK};
    color: ${COLORS.BLACK};
  }
`;

const Kbd = styled.span`
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 10px;
  font-family: monospace;
  margin-left: 4px;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  ${SaveButton}:hover &,
  ${CancelButton}:hover & {
    opacity: 1;
  }
`;

function AnnotationForm({
  isCreating, // Boolean: true when creating a new annotation, false when editing existing
  isEditing, // Boolean: true when editing an existing annotation
  annotationStart,
  annotationEnd,
  content,
  trialPart,
  emotion,
  onContentChange,
  onTrialPartChange,
  onEmotionChange,
  onSave,
  onCancel,
}) {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isCreating || isEditing) {
      // Auto-focus the textarea when creating new annotation or editing existing one
      // setTimeout ensures the DOM has fully rendered before attempting to focus
      // This improves UX by allowing users to start typing immediately
      const focusTimer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);

      // Cleanup timer to prevent memory leaks
      return () => clearTimeout(focusTimer);
    }
  }, [isCreating, isEditing]);

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (content.trim()) {
        onSave();
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <FormContainer>
      <FormHeader>
        <FormTitle variant="body1Regular">
          {isCreating ? 'Creating New Annotation' : 'Editing Annotation'}
        </FormTitle>
        <CloseButton onClick={onCancel}>×</CloseButton>
      </FormHeader>

      <FormGrid>
        <FormField>
          <Label>Start Time</Label>
          <TimeDisplay>{formatTime(annotationStart)}</TimeDisplay>
        </FormField>
        {/* End Time is only shown when editing existing annotation that has an end time */}
        {isEditing && annotationEnd && (
          <FormField>
            <Label>End Time</Label>
            <TimeDisplay>{formatTime(annotationEnd)}</TimeDisplay>
          </FormField>
        )}
      </FormGrid>

      <FormGrid>
        <FormField>
          <Label>Trial Part</Label>
          <Select value={trialPart} onChange={e => onTrialPartChange(e.target.value)}>
            {TRIAL_PARTS.map(part => (
              <option key={part.value} value={part.value}>
                {part.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField>
          <Label>Emotion</Label>
          <Select value={emotion} onChange={e => onEmotionChange(e.target.value)}>
            {ANNOTATION_EMOTION_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormField>
      </FormGrid>

      <FormField>
        <Label>Content</Label>
        <TextArea
          ref={textareaRef}
          value={content}
          onChange={e => onContentChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your annotation here... (Press Enter to save, Shift+Enter for new line)"
        />
      </FormField>

      <ButtonGroup>
        <SaveButton
          onClick={onSave}
          disabled={!content.trim()}
          title="Save annotation (Enter)"
        >
          <Save size={14} style={{ marginRight: '6px' }} />
          Save
          <Kbd>Enter</Kbd>
        </SaveButton>
        <CancelButton onClick={onCancel} title="Cancel (Escape)">
          Cancel <Kbd>Esc</Kbd>
        </CancelButton>
      </ButtonGroup>
    </FormContainer>
  );
}

AnnotationForm.propTypes = {
  isCreating: PropTypes.bool,
  isEditing: PropTypes.bool,
  annotationStart: PropTypes.number,
  annotationEnd: PropTypes.number,
  content: PropTypes.string.isRequired,
  trialPart: PropTypes.string.isRequired,
  emotion: PropTypes.string.isRequired,
  onContentChange: PropTypes.func.isRequired,
  onTrialPartChange: PropTypes.func.isRequired,
  onEmotionChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

AnnotationForm.defaultProps = {
  isCreating: false,
  isEditing: false,
  annotationStart: null,
  annotationEnd: null,
};

export default AnnotationForm;
